import { useMutation, useQuery } from '@apollo/client';
import {
	Button,
	SettingsGroup,
	SettingsItemAutocomplete,
	SettingsItemNumericField,
	SettingsItemText,
} from '@elipssolution/harfang';
import { styled } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useConfirmationDialogContext } from '../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../hooks/useSettingsDialog';
import {
	FetchCustomerFileType,
	FETCH_CUSTOMER_FILE,
	UpdateCustomerFileMaxAllowedSignatureInvitationsType,
	UpdateCustomerFileMaxAllowedSignatureInvitationsInputType,
	UPDATE_CUSTOMER_FILE_MAX_ALLOWED_SIGNATURE_INVITATIONS,
} from '../../../src/api/customerFile';
import { CustomerFileType } from '../../../types/customerFile';
import { ERROR_ANIMATION_DELAY } from '../../../utils/statusAnimationDelay';
import { COUNT_USED_SIGNATURE_INVITATIONS, FetchCountUsedSignatureInvitations } from '../api/signer';

const AddSignaturesSlotsButtonContainer = styled('div')({
	display: 'flex',
	justifyContent: 'flex-end',
});

const AddSignaturesSlotsButton = styled(Button)({
	minWidth: 'fit-content !important',
	width: 'fit-content !important',
});

type SignatureSlotsEditModeType = {
	id: string;
	label: string;
	successLabel: string;
};

const signatureSlotsEditModes: SignatureSlotsEditModeType[] = [
	{
		id: 'add',
		label: 'Ajouter',
		successLabel: 'Invitations ajoutées',
	},
	{
		id: 'remove',
		label: 'Retirer',
		successLabel: 'Invitations retirées',
	},
];

type FormFieldsType = {
	appliedQuantity: number;
	mode: SignatureSlotsEditModeType;
	quantity: number;
};

const formDefaultValues = {
	mode: signatureSlotsEditModes.find(({ id }) => id === 'add'),
};

type CustomerFileSectionProps = {
	customerFileId: CustomerFileType['id'];
};

const CustomerFileSection = ({ customerFileId }: CustomerFileSectionProps) => {
	const { onHasDirtyFormChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		watch,
		reset,
	} = useForm<FormFieldsType>({
		defaultValues: formDefaultValues,
	});

	const signatureSlotsEditModeFormValue = watch('mode');
	const signatureSlotsQuantityFormValue = watch('quantity');

	const [quantityFieldError, setQuantityFieldError] = useState<string>();

	const { data: customerFileFetchData } = useQuery<FetchCustomerFileType>(FETCH_CUSTOMER_FILE, {
		variables: {
			id: customerFileId,
		},
	});
	const { customerFile } = customerFileFetchData || {};
	const { maxAllowedSignatureInvitations: customerFileMaxAllowedSignatureInvitations, name: customerFileName } =
		customerFile || {};
	// Cannot fallback maxAllowedSignatureInvitations on 0 because "null" doesn't trigger fallback value
	const maxAllowedSignatureInvitations = customerFileMaxAllowedSignatureInvitations ?? 0;

	const { data: countUsedSignatureInvitationsFetchedData } = useQuery<FetchCountUsedSignatureInvitations>(
		COUNT_USED_SIGNATURE_INVITATIONS,
		{
			variables: {
				id: customerFileId,
			},
		},
	);
	const { sign_countUsedSignatureInvitations: countUsedSignatureInvitations = 0 } =
		countUsedSignatureInvitationsFetchedData || {};

	const [
		updateCustomerFileMaxAllowedSignatureInvitations,
		{
			data: updateCustomerFileMaxAllowedSignatureInvitationsData,
			reset: resetUpdateCustomerFileMaxAllowedSignatureInvitationsMutationState,
		},
	] = useMutation<
		UpdateCustomerFileMaxAllowedSignatureInvitationsType,
		UpdateCustomerFileMaxAllowedSignatureInvitationsInputType
	>(UPDATE_CUSTOMER_FILE_MAX_ALLOWED_SIGNATURE_INVITATIONS);

	const onSubmit = useCallback(
		async ({ appliedQuantity, mode, quantity }: FormFieldsType) => {
			const { data } = await updateCustomerFileMaxAllowedSignatureInvitations({
				variables: {
					id: customerFileId,
					maxAllowedSignatureInvitations: mode.id === 'add' ? appliedQuantity + quantity : appliedQuantity - quantity,
				},
			});

			const { updateCustomerFileMaxAllowedSignatureInvitations: updatedCustomerFile } = data ?? {};
			const { maxAllowedSignatureInvitations: updatedMaxAllowedSignatureInvitations } = updatedCustomerFile ?? {};

			reset({
				mode,
				appliedQuantity: updatedMaxAllowedSignatureInvitations,
			});

			setTimeout(() => {
				resetUpdateCustomerFileMaxAllowedSignatureInvitationsMutationState();
			}, ERROR_ANIMATION_DELAY);
		},
		[
			updateCustomerFileMaxAllowedSignatureInvitations,
			customerFileId,
			reset,
			resetUpdateCustomerFileMaxAllowedSignatureInvitationsMutationState,
		],
	);

	const editModeDataSource = useCallback(
		async (): Promise<{
			count: number;
			items: SignatureSlotsEditModeType[];
		}> =>
			Promise.resolve({
				count: signatureSlotsEditModes.length,
				items: signatureSlotsEditModes,
			}),
		[],
	);

	const handleFormSubmission = async () => {
		if (!customerFileName) return undefined;

		const isFormSubmissionConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir ${signatureSlotsEditModeFormValue.label.toLowerCase()} ${signatureSlotsQuantityFormValue} invitations à signer ${
				signatureSlotsEditModeFormValue.id === 'add' ? 'au' : 'du'
			} dossier ${customerFileName} ?`,
		});

		if (!isFormSubmissionConfirmed) return null;

		return handleSubmit(onSubmit)();
	};

	const checkQuantityBounds = useCallback(
		(value: number): boolean => {
			const base = maxAllowedSignatureInvitations - countUsedSignatureInvitations;
			const total = signatureSlotsEditModeFormValue.id === 'add' ? base + value : base - value;
			const actionLabel = signatureSlotsEditModeFormValue.id === 'add' ? 'cet ajout' : 'ce retrait';

			if (total < 0) {
				setQuantityFieldError(`Le cumul sera négatif avec ${actionLabel} (${total}).`);

				return false;
			}

			setQuantityFieldError(undefined);

			return true;
		},
		[maxAllowedSignatureInvitations, countUsedSignatureInvitations, signatureSlotsEditModeFormValue.id],
	);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
	}, [isDirty, onHasDirtyFormChange]);

	useEffect(
		() =>
			reset({
				...formDefaultValues,
				appliedQuantity: maxAllowedSignatureInvitations,
			}),
		[maxAllowedSignatureInvitations, reset],
	);

	return (
		<SettingsDialogPage title={customerFileName}>
			<SettingsGroup>
				<Controller
					name="appliedQuantity"
					control={control}
					render={({ field: { value } }) => (
						<SettingsItemText
							sx={{
								'& > div:last-of-type > p:last-of-type': {
									textAlign: 'right',
								},
							}}
							text={`${value}`}
							label="Total d’invitations à signer allouées au dossier"
							description="Ce nombre correspond au total des invitations achetées pour ce dossier depuis sa création."
						/>
					)}
				/>
			</SettingsGroup>
			<SettingsGroup label="Modifications du nombre d’invitations à signer">
				<Controller
					name="mode"
					control={control}
					rules={{
						required: true,
					}}
					render={({ field }) => (
						<SettingsItemAutocomplete
							{...field}
							dataSource={editModeDataSource}
							label="Mode de modification"
							getOptionLabel={({ label }) => label}
							disableClearable
						/>
					)}
				/>
				<Controller
					name="quantity"
					control={control}
					rules={{
						required: true,
						min: 1,
						validate: checkQuantityBounds,
					}}
					render={({ field, fieldState: { error } }) => (
						<SettingsItemNumericField
							{...field}
							invalid={!!error}
							helperText={signatureSlotsQuantityFormValue && quantityFieldError ? quantityFieldError : ' '}
							label={`Nombre d'invitations à ${signatureSlotsEditModeFormValue.label.toLowerCase()}`}
							allowNegative={false}
							decimalScale={0}
						/>
					)}
				/>
			</SettingsGroup>

			<AddSignaturesSlotsButtonContainer>
				<AddSignaturesSlotsButton disabled={!isValid || !isDirty} onClick={handleFormSubmission} variant="contained">
					{updateCustomerFileMaxAllowedSignatureInvitationsData
						? signatureSlotsEditModeFormValue.successLabel
						: signatureSlotsEditModeFormValue.label}
				</AddSignaturesSlotsButton>
			</AddSignaturesSlotsButtonContainer>
		</SettingsDialogPage>
	);
};

export default CustomerFileSection;
